"use client";

import { Fragment, useState } from "react";
import { useIsDark } from "@/context/ThemeContext";
import Image from "next/image";
import Link from "next/link";
import { links1, links2, links3, offices } from "@/constants/Footer";
import NewsletterCard from "@/components/NewsletterCard";

const X = ({ isdark }) => (
  <Image
    src={isdark ? "/assets/footers/x.svg" : "/assets/footers/twitter.light.svg"}
    alt="X (Twitter)"
    width={32}
    height={32}
    loading="lazy"
  />
);

const Linkedin = ({ isdark }) => (
  <Image
    src={
      isdark
        ? "/assets/footers/linkedinl.svg"
        : "/assets/footers/linkedin.light.svg"
    }
    alt="LinkedIn"
    width={32}
    height={32}
    loading="lazy"
  />
);

const Discord = ({ isdark }) => (
  <Image
    src={
      isdark
        ? "/assets/footers/discordl.svg"
        : "/assets/footers/discord.light.svg"
    }
    alt="Discord"
    width={32}
    height={32}
    loading="lazy"
  />
);

const FB = ({ isdark }) => (
  <Image
    src={isdark ? "/assets/footers/fb.svg" : "/assets/footers/fb.light.svg"}
    alt="fb"
    width={32}
    height={32}
    loading="lazy"
  />
);

const Instagram = ({ isdark }) => (
  <Image
    src={
      isdark ? "/assets/footers/insta.svg" : "/assets/footers/insta.light.svg"
    }
    alt="insta"
    width={32}
    height={32}
    loading="lazy"
  />
);

const LeftSection = ({ isdark }) => {
  return (
    <div className="flex flex-col items-center md:items-start text-center font-inter font-normal">
      <Link href="/" className="relative z-50">
        <Image
          src={
            isdark ? "/assets/logo/logoDark.svg" : "/assets/logo/logoLight.svg"
          }
          alt="ICIEOS Logo"
          width={147}
          height={43}
          className="w-25 xl:w-[147px]"
          priority={false}
          loading="lazy"
        />
      </Link>
      <p className="text-[12px] lg:text-sm dark:text-[#FFFFFF] text-[#3B3B3B]">
        Global Catalysts for Innovation
      </p>

      <Link
        href="/schedule"
        className="text-center font-albert-sans font-semibold text-sm text-[#FF1CF7] border border-[#FF1CF7] rounded-[7px] cursor-pointer py-1 px-3 lg:py-3 lg:px-5 mt-5"
      >
        Schedule a Strategy Call
      </Link>

      <div className="w-full text-center md:text-left mt-3">
        <div className="flex flex-col gap-3 w-full py-3">
          {offices.map((office, idx) => (
            <Fragment key={office.country}>
              {idx > 0 && (
                <div className="h-px w-full rounded-[100px] bg-[#FF1CF733]" />
              )}
              <div className="flex flex-col gap-2 w-full">
                <p className="font-manrope font-bold text-[12px] lg:text-[14px] text-[#17121F] dark:text-[#FCFDFFEF]">
                  {office.country}
                </p>
                <p className="font-manrope font-normal text-[10px] lg:text-[12px] leading-[18px] text-[#665F70] dark:text-[#F1F7FEB5]">
                  {office.address}
                </p>
              </div>
            </Fragment>
          ))}
        </div>
      </div>

      {/* Pass isdark into icons */}
      <div className="flex gap-4 footer-socialsection justify-center lg:justify-start mt-3">
        <FooterIcon
          icon={<Linkedin isdark={isdark} />}
          isdark={isdark}
          href="https://www.linkedin.com/company/icieos/"
        />
        <FooterIcon
          icon={<X isdark={isdark} />}
          isdark={isdark}
          href="https://x.com/icieos"
        />
        <FooterIcon
          icon={<FB isdark={isdark} />}
          isdark={isdark}
          href="https://www.facebook.com/icieosMVPdevelopers"
        />
        <FooterIcon
          icon={<Instagram isdark={isdark} />}
          isdark={isdark}
          href="https://www.instagram.com/icieos__"
        />
        <FooterIcon
          icon={<Discord isdark={isdark} />}
          isdark={isdark}
          href="https://discord.gg/anc2fQQk4d"
        />
      </div>

      <div className="flex items-center gap-3 mt-5">
        <Link
          href="https://thecpdregister.com"
          target="_blank"
          rel="noopener noreferrer"
        >
          <Image
            src="/assets/footer/cpd_approved.webp"
            alt="The CPD Group Approved Provider #791092"
            width={1552}
            height={531}
            className="w-[140px] lg:w-40 h-auto"
            sizes="160px"
            loading="lazy"
          />
        </Link>
        <Link
          href="/assets/footer/cpd_certificate.webp"
          target="_blank"
          rel="noopener noreferrer"
        >
          <Image
            src="/assets/footer/cpd_certificate.webp"
            alt="ICIEOS (Pvt) Ltd CPD Provider Certificate"
            width={3508}
            height={2480}
            className="w-[68px] lg:w-[78px] h-auto rounded-[4px] border border-[#E8A33D]"
            sizes="80px"
            loading="lazy"
          />
        </Link>
      </div>
    </div>
  );
};

export default function Footer() {
  const [openSection, setOpenSection] = useState(null);

  const toggleSection = (section) => {
    setOpenSection((prevSection) => (prevSection === section ? null : section));
  };
  const isdark = useIsDark();

  return (
    <footer
      className={`${
        isdark ? "bg-[#000000]" : "bg-[#F7F2FF]"
      } text-white font-inter relative font-normal pt-18`}
      style={{ minHeight: "448px", height: "auto", contain: "layout" }}
    >
      <div className="justify-center items-center max-w-[2000px] mx-5 xl:mx-[100px] 3xl:mx-[150] 5xl:mx-auto">
        {/* Gradient overlay*/}
        <div
          className="absolute z-10 top-0 left-0 w-full h-32 pointer-events-none"
          style={{
            background: isdark
              ? "radial-gradient(70.71% 80.71% at 50% 0%, rgba(200, 200, 200, 0.1) 0%, rgba(0, 0, 0, 0) 80%)"
              : "radial-gradient(50.71% 100% at 50% 0%, rgb(223, 39, 223, 0.2) 0%, rgba(0, 0, 0, 0) 60%)",
          }}
        >
          {/* Gradient border line */}
          <div
            className="absolute top-0 left-0 w-full h-px"
            style={{
              background: isdark
                ? "linear-gradient(90deg, rgba(0,0,0,0) 30%, rgba(255,255,255,0.35) 50%, rgba(0,0,0,0) 70%)"
                : "linear-gradient(90deg, rgba(0,0,0,0) 30%, rgba(61, 0, 114, 0.35) 50%, rgba(0,0,0,0) 70%)",
            }}
          ></div>
        </div>

        {/* Content container */}
        <div className="relative z-10 px-1 mobile:px-4 sm:px-6 md:px-3 tablet:px-6 lg:px-12 xl:px-20">
          {/* Mobile Layout (sm) */}
          <div className="block md:hidden mb-10">
            {/* Address and Social Icons */}
            <div className="flex flex-col items-center text-center">
              <LeftSection isdark={isdark} />
            </div>

            {/* Newsletter Card - Mobile */}
            <div className="flex justify-center my-6">
              <NewsletterCard isdark={isdark} />
            </div>

            {/* Collapsible Sections */}
            <div className="space-y-4 my-4">
              <CollapsibleSection
                heading="Quick Links"
                links={links1}
                isOpen={openSection === "Quick Links"}
                onToggle={() => toggleSection("Quick Links")}
                isdark={isdark}
              />
              <CollapsibleSection
                heading="Services"
                links={links2}
                isOpen={openSection === "Services"}
                onToggle={() => toggleSection("Services")}
                isdark={isdark}
              />
              <CollapsibleSection
                heading="Legal"
                links={links3}
                isOpen={openSection === "legal"}
                onToggle={() => toggleSection("legal")}
                isdark={isdark}
              />
            </div>
          </div>
        </div>

        {/* Desktop Layout - Centered */}
        <div className="relative z-10">
          <div className="hidden md:block">
            <div className="mx-auto">
              <div className="flex flex-wrap justify-between items-start gap-6 tablet:gap-10 lg:gap-15 laptop:gap-26 3xl:gap-25">
                {/* Left section - Address and Social */}
                <div className="shrink-0">
                  <LeftSection isdark={isdark} />
                </div>

                {/* Right section - Footer Links */}
                <div className="grid grid-cols-3 lg:gap-2 3xl:gap-30 flex-1 justify-between leading-relaxed min-w-0">
                  <div className="flex flex-col">
                    <FooterColumn
                      heading="Quick Links"
                      links={links1}
                      isdark={isdark}
                    />
                  </div>
                  <div className="flex flex-col">
                    <FooterColumn
                      heading="Services"
                      links={links2}
                      isdark={isdark}
                    />
                  </div>
                  <div className="flex flex-col">
                    <FooterColumn
                      heading="Legal"
                      links={links3}
                      isdark={isdark}
                    />
                    {/* Newsletter Card below Legal column */}
                    <div className="mt-6">
                      <NewsletterCard />
                    </div>
                  </div>
                </div>
              </div>
              <p className="text-center font-light font-inter text-[12px] lg:text-sm dark:text-[#FFFFFF] text-[#3B3B3B] leading-5 mt-[50px] mb-4">
                Copyright© 2026. All rights reserved by ICIEOS.
              </p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

function CollapsibleSection({ heading, links, isOpen, onToggle, isdark }) {
  return (
    <div className="">
      <button
        onClick={onToggle}
        className="w-full flex justify-between items-center py-2 text-left px-4 mb-4 dark:text-[#FCFDFFED] text-[#3B3B3B] dark:data-[open=true]:bg-[#393e4639] dark:data-[open=true]:border-[0.5px] dark:data-[open=true]:border-[#fcfdff61] data-[open=true]:bg-[#E5D4FF] data-[open=true]:border-[0.5px] data-[open=true]:border-[#03030361]"
        data-open={isOpen}
      >
        <h1 className="font-inter font-normal not-italic text-[14px] leading-5 align-middle dark:text-[#FCFDFFED] text-[#3B3B3B]">
          {heading}
        </h1>

        <div className="font-light w-6 h-6 flex items-center justify-center">
          {isOpen ? "−" : "+"}
        </div>
      </button>

      {isOpen && (
        <div className="pb-4 dark:bg-black bg-[#F7F2FF]">
          <ul className="space-y-3 px-4">
            {links.map((link, idx) => (
              <li key={idx}>
                <Link
                  href={link.href}
                  className="cursor-pointer inline font-inter font-normal text-[14px] transition-colors duration-200 dark:text-[#F1F7FEB5] dark:hover:text-white text-[#3B3B3B] hover:text-[#FF1CF7]"
                >
                  {link.label}
                </Link>
              </li>
            ))}
          </ul>
          {/* Gradient below stroke */}
          <div
            className="w-full h-px mt-4"
            style={{
              background: isdark
                ? "linear-gradient(90deg, rgba(0,0,0,0) 0%, rgba(255,255,255,0.35) 50%, rgba(0,0,0,0) 100%)"
                : "linear-gradient(90deg, rgba(0,0,0,0) 30%, rgba(61, 0, 114, 0.35) 50%, rgba(0,0,0,0) 70%)",
            }}
          ></div>
        </div>
      )}
    </div>
  );
}

function FooterColumn({ heading, links, isdark }) {
  return (
    <div className="font-inter font-normal not-italic text-[10px] tablet:text-[12px] lg:text-sm leading-5 ">
      <h1 className="font-semibold mb-6 leading-5 dark:text-[#FCFDFFEF] text-[#3B3B3B]">
        {heading}
      </h1>

      <ul className="space-y-3 mt-8">
        {links.map((link, idx) => (
          <li key={idx}>
            <Link
              href={link.href}
              className="cursor-pointer inline transition-colors duration-200 dark:text-[#F1F7FEB5] dark:hover:text-white text-[#3B3B3B] hover:text-[#FF1CF7]"
            >
              {link.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

function FooterIcon({ icon, href, isdark }) {
  const content = (
    <div className="w-8 h-8 sm:w-10 sm:h-10 md:w-8 md:h-8 rounded-full flex items-center justify-center transition-colors duration-300 cursor-pointer dark:bg-[#111111] dark:hover:bg-[#222222] dark:active:bg-[#222222] bg-[#F7F2FF] hover:bg-[#E5D4FF] active:bg-[#E5D4FF]">
      {icon}
    </div>
  );

  if (href) {
    return (
      <Link href={href} target="_blank" rel="noopener noreferrer">
        {content}
      </Link>
    );
  }

  return content;
}
